import {
  createContainer,
  InjectionMode,
  asFunction,
  asClass,
  asValue,
  Constructor,
  BuildResolverOptions,
  Resolver,
  AwilixContainer
} from "awilix";

import IModuleContainer from "./IModuleContainer";
import Application from "../Application";

import HomeArea from "../core/areas/HomeArea";

import CodebookArea from "../codebooks/areas/GenericCodebookArea";
import CodebookService from "../codebooks/service/CodebookService";
import AuthenticationArea from "../authentication/areas/AuthenticationArea";
import AuthenticationService from "../authentication/service/AuthenticationService";
import CredentialsService from "../authentication/service/CredentialsService";
import CreateResourceInteractor from "../core/interactor/app/crud/CreateResourceInteractor";
import CreateListResourceInteractor from "../core/interactor/app/crud/CreateListResourceInteractor";
import DeleteResourceInteractor from "../core/interactor/app/crud/DeleteResourceInteractor";
import DeleteListResourceInteractor from "../core/interactor/app/crud/DeleteListResourceInteractor";
import FindResourceEntryInteractor from "../core/interactor/app/crud/FindResourceEntryInteractor";
import GetAllResourceEntriesInteractor from "../core/interactor/app/crud/GetResourceEntriesInteractor";
import GetResourceTextValueInteractor from "../core/interactor/app/crud/GetResourceTextValueInteractor";
import UpdateResourceInteractor from "../core/interactor/app/crud/UpdateResourceInteractor";
import HandleApplicationErrorInteractor from "../core/interactor/app/HandleApplicationErrorInteractor";
import InteractorProxy from "../core/interactor/InteractorProxy";
import ShowHomeInteractor from "../core/interactor/main/ShowHomeInteractor";
import ShowErrorMessageInteractor from "../core/interactor/notifications/ShowErrorMessageInteractor";
import ShowInfoMessageInteractor from "../core/interactor/notifications/ShowInfoMessageInteractor";
import ShowSuccessMessageInteractor from "../core/interactor/notifications/ShowSuccessMessageInteractor";
import ShowWarningMessageInteractor from "../core/interactor/notifications/ShowWarningMessageInteractor";
import { InMemoryCacheManager } from "../core/model/runtime/cache/InMemoryCacheManager";
import Dispatcher from "../core/model/runtime/state/Dispatcher";
import CryptographyProvider from "../core/model/security/CryptographyProvider";
import RandomValueProvider from "../core/model/security/RandomValueProvider";
import CachingHttpService from "../core/service/CachingHttpService";
import CookieStorageService from "../core/service/CookieStorageService";
import CryptographyService from "../core/service/cryptography/CryptographyService";
import HttpService from "../core/service/HttpService";
import TranslationService from "../core/service/locale/TranslationService";
import NotificationService from "../core/service/notification/NotificationService";
import SimpleCRUDServiceFactory from "../core/service/SimpleCRUDServiceFactory";
import StorageService from "../core/service/StorageService";
import UserService from "../user/service/UserService";
import Store from "../core/model/runtime/state/Store";
import CaseLawDocumentsArea from "../caseLawDocuments/areas/CaseLawDocumentsArea";
import IndexTypeArea from "../indexTypes/area/IndexTypeArea";
import IndexTypeGroupService from "../indexTypes/service/IndexGroupService";
import OrganisationIndexDataArea from "../organisations/areas/OrganisationIndexDataArea";
import CreateFileResourceInteractor from "../core/interactor/app/crud/CreateFileResourceInteractor";
import SearchArea from "../search/areas/SearchArea";
import OrganisationArea from "../organisations/areas/OrganisationArea";
import CaseDecisionsArea from "../caseDecisions/area/CaseDecisionsArea";
import OrganisationLegalViewTypesArea from "../organisations/areas/OrganisationLegalViewTypesArea";
import OrganisationCaseDecisionTypesArea from "../organisations/areas/OrganisationCaseDecisionTypesArea";
import CaseDecisionService from "../caseDecisions/service/CaseDecisionService";
import CaseLawDocumentService from "../caseLawDocuments/service/CaseLawDocumentService";
import AttachmentTypeArea from "../attachmentType/area/AttachmentTypeArea";
import PortalArea from "../portal/areas/PortalArea";
import ShowPortalInteractor from "../portal/interactors/ShowPortalInteractor";
import ReportArea from "../report/area/ReportArea";
import LegalViewArea from "../legalViews/area/LegalViewArea";

import ChangeLanguageInteractor from "../core/interactor/language/ChangeLanguageInteractor";
import LegalViewService from "../legalViews/service/LegalViewService";
import LanguageService from "../core/service/language/LanguageService";
import GetSelectedLanguageInteractor from "../core/interactor/language/GetSelectedLanguageInteractor";
import LoadDefaultStateInteractor from "../core/interactor/language/LoadDefaultStateInteractor";
import { UserPreferences } from "../core/common/Constants";
import AddDocumentTypeVisibilityLevelsInteractor from "../documentTypes/interactors/AddDocumentTypeVisibilityLevelsInteractor";
import GetDocumentTypeVisibilityLevelInteractor from "../documentTypes/interactors/GetDocumentTypeVisibilityLevelsInteractor";
import DeleteDocumentTypeFromOrganisationInteractor from "../documentTypes/interactors/DeleteDocumentTypeFromOrganisationInteractor";
import GetAllLanguagesInteractor from "../core/interactor/language/GetAllLanguagesInteractor";
import ShowErrorMessageWithActionButtonInteractor from "../core/interactor/notifications/ShowErrorMessageWithActionButtonInteractor";
import LocaleProvider from "../core/service/locale/LocaleProvider";
import ConsoleTrackingPlugin from "../core/plugin/tracking/ConsoleTrackingPlugin";
import GoogleAnalyticsTrackingPlugin from "../core/plugin/tracking/GoogleAnalyticsTrackingPlugin";
import { TrackingService } from "../core/service/TrackingService";
import NoOpTrackingPlugin from "../core/plugin/tracking/NoOpTrackingPlugin";

const ModuleContainer = (
  application: Application,
  container?: any
): IModuleContainer => {
  const _application = application;
  const _container =
    container ||
    createContainer({
      injectionMode: InjectionMode.PROXY
    });

  function withCache(moduleName: string, timeToLiveMs?: number) {
    return (c) => ({
      httpService: CachingHttpService({
        http: _container.resolve("http"),
        cacheManager: _container.resolve("cacheManager"),
        options: {
          name: moduleName,
          timeToLive: timeToLiveMs !== undefined ? timeToLiveMs : 0
        }
      })
    });
  }

  function asInteractor<T = {}>(
    Type: Constructor<T>,
    opts?: BuildResolverOptions<T>
  ): Resolver<T> {
    return {
      resolve: (container: AwilixContainer) => {
        var interactor = container.build(asClass(Type, opts));
        return new InteractorProxy({
          interactor,
          handleApplicationError:
            container.resolve<HandleApplicationErrorInteractor>(
              "handleApplicationError"
            ),
          application: _application
        }) as any as T;
      }
    };
  }

  const Config = import.meta.env;
  _container.register({
    // config: asFunction(Configuration).singleton(),
    // configurationProvider: asFunction(
    //   EnvironmentConfigurationProvider
    // ).singleton(),
    application: asValue(_application),
    container: asValue(_container),
    trackingId: asValue(Config.VITE_TRACKING_ID),
    baseUrl: asValue(Config.VITE_API_URL),
    options: asValue(null),
    cacheManager: asFunction(InMemoryCacheManager).singleton(),
    localeProvider: asFunction(LocaleProvider).singleton(),
    language: asValue(UserPreferences.DEFAULT_LANGUAGE),
    dispatcher: asFunction(Dispatcher).singleton(),
    handleApplicationError: asClass(HandleApplicationErrorInteractor),
    store: asFunction(Store)
      .singleton()
      .inject((c) => ({
        dispatcher: _container.resolve("dispatcher"),
        defaultTopic: "app",
        initialState: {}
      })),
    defaultSettings: asValue(Config)
  });

  //services
  _container.register({
    storageService: asFunction(StorageService),
    cookieStorageService: asFunction(CookieStorageService),
    httpService: asFunction(CachingHttpService),
    http: asFunction(HttpService),
    translationService: asFunction(TranslationService),
    cryptographyProvider: asFunction(CryptographyProvider),
    randomValueProvider: asFunction(RandomValueProvider),
    cryptographyService: asFunction(CryptographyService),
    notificationService: asFunction(NotificationService),
    userService: asFunction(UserService),
    languageService: asFunction(LanguageService),
    credentialsService: asFunction(CredentialsService),
    authenticationService: asFunction(AuthenticationService).inject(
      withCache("authentication")
    ),
    simpleCRUDServiceFactory: asFunction(SimpleCRUDServiceFactory).inject(
      withCache("CRUD", -1)
    ),
    indexTypeGroupService: asFunction(IndexTypeGroupService).inject(
      withCache("CRUD", -1)
    ),
    caseDecisionService: asFunction(CaseDecisionService).inject(
      withCache("CRUD", -1)
    ),
    caseLawDocumentService: asFunction(CaseLawDocumentService).inject(
      withCache("CRUD", -1)
    ),
    legalViewService: asFunction(LegalViewService).inject(withCache("CRUD", -1))
  });

  //areas
  _container.register({
    homeArea: asClass(HomeArea).singleton(),
    authenticationArea: asClass(AuthenticationArea).singleton(),
    codebookArea: asClass(CodebookArea).singleton(),
    caseLawDocumentsArea: asClass(CaseLawDocumentsArea).singleton(),
    indexTypeArea: asClass(IndexTypeArea).singleton(),
    organisationIndexDataArea: asClass(OrganisationIndexDataArea).singleton(),
    searchArea: asClass(SearchArea).singleton(),
    caseDecisionsArea: asClass(CaseDecisionsArea).singleton(),
    organisationArea: asClass(OrganisationArea).singleton(),
    portalArea: asClass(PortalArea).singleton(),
    attachmentTypeArea: asClass(AttachmentTypeArea).singleton(),
    reportArea: asClass(ReportArea).singleton(),
    legalViewArea: asClass(LegalViewArea).singleton(),
    organisationCaseDecisionTypesArea: asClass(
      OrganisationCaseDecisionTypesArea
    ).singleton(),
    organisationLegalViewTypesArea: asClass(
      OrganisationLegalViewTypesArea
    ).singleton()
  });

  //interactors
  _container.register({
    showHome: asInteractor(ShowHomeInteractor),
    showPortal: asInteractor(ShowPortalInteractor),
    showSuccessMessage: asInteractor(ShowSuccessMessageInteractor),
    showErrorMessage: asInteractor(ShowErrorMessageInteractor),
    showErrorMessageWithActionButton: asInteractor(
      ShowErrorMessageWithActionButtonInteractor
    ),
    showWarningMessage: asInteractor(ShowWarningMessageInteractor),
    showInfoMessage: asInteractor(ShowInfoMessageInteractor),
    getAllResourceEntries: asInteractor(GetAllResourceEntriesInteractor),
    createResource: asInteractor(CreateResourceInteractor),
    createListResource: asInteractor(CreateListResourceInteractor),
    updateResource: asInteractor(UpdateResourceInteractor),
    deleteResource: asInteractor(DeleteResourceInteractor),
    deleteListResource: asInteractor(DeleteListResourceInteractor),
    findResourceEntry: asInteractor(FindResourceEntryInteractor),
    getResourceTextValue: asInteractor(GetResourceTextValueInteractor),
    createFileResource: asInteractor(CreateFileResourceInteractor),
    changeLanguage: asInteractor(ChangeLanguageInteractor),
    getSelectedLanguage: asInteractor(GetSelectedLanguageInteractor),
    getAllLanguages: asInteractor(GetAllLanguagesInteractor),
    loadDefaultState: asInteractor(LoadDefaultStateInteractor),
    getDocumentTypeVisibilityLevel: asInteractor(
      GetDocumentTypeVisibilityLevelInteractor
    ),
    addDocumentTypeVisibilityLevels: asInteractor(
      AddDocumentTypeVisibilityLevelsInteractor
    ),
    deleteDocumentTypeFromOrganisation: asInteractor(
      DeleteDocumentTypeFromOrganisationInteractor
    )
  });

  //generic codebooks
  _container.register({
    codebookService: asFunction(CodebookService).inject(
      withCache("codebook", -1)
    )
  });

  //tracking
  _container.register({
    trackingPlugin: Config.DEV
      ? asFunction(NoOpTrackingPlugin)
      : asFunction(GoogleAnalyticsTrackingPlugin),
    trackingService: asFunction(TrackingService)
  });

  return {
    asInteractor,
    asFunction,
    register: _container.register,
    resolve<T>(name: string) {
      return _container.resolve(name) as T;
    },
    registerValue(name: string, dependency: any) {
      _container.register(name, asValue(dependency));
    },
    registerClass(name: string, dependency: any) {
      _container.register(name, asClass(dependency));
    },
    registerInteractor(name: string, dependency: any) {
      _container.register(name, asInteractor(dependency));
    },
    registerFunction(name: string, dependency: any) {
      _container.register(name, asFunction(dependency));
    },
    createScope() {
      return ModuleContainer(_application, _container.createScope());
    }
  } as IModuleContainer;
};

export default ModuleContainer;
